<template>
  <div class="bg-gray-50 min-h-screen">
    <Header />

    <div class="flex min-h-[calc(100vh-80px)] relative">
      <!-- MOBILE MENU BUTTON -->

      <button
        v-if="!showSidebar"
        @click="showSidebar = true"
        class="md:hidden fixed top-[80px] left-0 z-50 bg-blues text-white p-2 rounded-r-lg shadow-lg"
        aria-label="Open menu"
      >
        <svg
          class="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M4 6h16M4 12h16M4 18h16"
          />
        </svg>
      </button>

      <!-- MOBILE OVERLAY -->
      <div
        v-if="showSidebar"
        @click="showSidebar = false"
        class="fixed inset-0 bg-black bg-opacity-30 z-30 md:hidden"
      ></div>

      <!-- SIDEBAR -->
      <div
        :class="[
          'fixed md:sticky top-[80px] md:top-0 left-0 h-[calc(100vh-80px)] w-64 sm:w-72 md:w-64 lg:w-72 bg-white border-r border-gray-200 z-40 transform transition-transform duration-300 overflow-y-auto overscroll-contain',
          showSidebar ? 'translate-x-0' : '-translate-x-full',
          'md:translate-x-0',
        ]"
      >
        <!-- CLOSE BUTTON (MOBILE) - flush bar, no gap above -->
        <div
          class="sticky top-0 bg-white z-10 flex justify-end md:hidden px-3 py-2 border-b border-gray-100"
        >
          <button @click="showSidebar = false" aria-label="Close menu">
            <svg
              class="w-6 h-6 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <nav class="space-y-1 sm:space-y-2 p-4 pb-8">
          <!-- LOGIN -->
          <button
            @click="switchTab('login')"
            :class="[
              'flex w-full items-center gap-3 px-3 sm:px-4 py-2 text-sm font-medium rounded-lg transition-colors',
              activeTab === 'login'
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-700 hover:bg-gray-100',
            ]"
          >
            <svg
              class="w-5 h-5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l3 3m0 0l-3 3m3-3H3"
              />
            </svg>
            <span class="truncate">login</span>
          </button>

          <!-- HOME -->
          <button
            @click="switchTab('home')"
            :class="[
              'flex w-full items-center gap-3 px-3 sm:px-4 py-2 text-sm font-medium rounded-lg transition-colors',
              activeTab === 'home'
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-700 hover:bg-gray-100',
            ]"
          >
            <svg
              class="w-5 h-5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
              />
            </svg>
            <span class="truncate">Home</span>
          </button>

          <!-- COURSE -->
          <button
            @click="switchTab('course')"
            :class="[
              'flex w-full items-center gap-3 px-3 sm:px-4 py-2 text-sm font-medium rounded-lg transition-colors',
              activeTab === 'course'
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-700 hover:bg-gray-100',
            ]"
          >
            <svg
              class="w-5 h-5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222"
              />
            </svg>
            <span class="truncate">course</span>
          </button>

          <!-- LESSON -->
          <button
            @click="switchTab('student')"
            :class="[
              'flex w-full items-center gap-3 px-3 sm:px-4 py-2 text-sm font-medium rounded-lg transition-colors',
              activeTab === 'student'
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-700 hover:bg-gray-100',
            ]"
          >
            <svg
              class="w-5 h-5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
              />
            </svg>
            <span class="truncate">student</span>
          </button>

          <!-- MESSAGE -->
          <button
            @click="switchTab('message')"
            :class="[
              'flex w-full items-center gap-3 px-3 sm:px-4 py-2 text-sm font-medium rounded-lg transition-colors',
              activeTab === 'message'
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-700 hover:bg-gray-100',
            ]"
          >
            <svg
              class="w-5 h-5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm3.75 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z"
              />
            </svg>
            <span class="truncate">message</span>
          </button>

          <!-- ASSIGNMENT -->
          <button
            @click="switchTab('assignment')"
            :class="[
              'flex w-full items-center gap-3 px-3 sm:px-4 py-2 text-sm font-medium rounded-lg transition-colors',
              activeTab === 'assignment'
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-700 hover:bg-gray-100',
            ]"
          >
            <svg
              class="w-5 h-5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08M9.75 4.5a2.25 2.25 0 100-4.5 2.25 2.25 0 000 4.5zM8.25 8.25H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z"
              />
            </svg>
            <span class="truncate">assignment</span>
          </button>

          <!-- FORUMS -->
          <button
            @click="switchTab('forums')"
            :class="[
              'flex w-full items-center gap-3 px-3 sm:px-4 py-2 text-sm font-medium rounded-lg transition-colors',
              activeTab === 'forums'
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-700 hover:bg-gray-100',
            ]"
          >
            <svg
              class="w-5 h-5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"
              />
            </svg>
            <span class="truncate">Forums</span>
          </button>

          <!-- LIVE CLASS -->
          <button
            @click="switchTab('liveclass')"
            :class="[
              'flex w-full items-center gap-3 px-3 sm:px-4 py-2 text-sm font-medium rounded-lg transition-colors',
              activeTab === 'liveclass'
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-700 hover:bg-gray-100',
            ]"
          >
            <svg
              class="w-5 h-5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z"
              />
            </svg>
            <span class="truncate">Live classes</span>
          </button>

          <!-- ASSESSMENT -->
          <button
            @click="switchTab('assessment')"
            :class="[
              'flex w-full items-center gap-3 px-3 sm:px-4 py-2 text-sm font-medium rounded-lg transition-colors',
              activeTab === 'assessment'
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-700 hover:bg-gray-100',
            ]"
          >
            <svg
              class="w-5 h-5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
              />
            </svg>
            <span class="truncate">Assessment</span>
          </button>

          <!-- PROJECT / TEAM LEADER -->
          <button
            @click="switchTab('project')"
            :class="[
              'flex w-full items-center gap-3 px-3 sm:px-4 py-2 text-sm font-medium rounded-lg transition-colors',
              activeTab === 'project'
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-700 hover:bg-gray-100',
            ]"
          >
            <svg
              class="w-5 h-5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0"
              />
            </svg>
            <span class="truncate">project</span>
          </button>
          <!-- grading -->
          <button
            @click="switchTab('grading')"
            :class="[
              'flex w-full items-center gap-3 px-3 sm:px-4 py-2 text-sm font-medium rounded-lg transition-colors',
              activeTab === 'grading'
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-700 hover:bg-gray-100',
            ]"
          >
            <svg
              class="w-5 h-5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span class="truncate">grading</span>
          </button>
          <!-- lessons polls -->
          <button
            @click="switchTab('lesson polls')"
            :class="[
              'flex w-full items-center gap-3 px-3 sm:px-4 py-2 text-sm font-medium rounded-lg transition-colors',
              activeTab === 'lesson polls'
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-700 hover:bg-gray-100',
            ]"
          >
            <svg
              class="w-5 h-5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
              />
            </svg>
            <span class="truncate">lesson polls</span>
          </button>
          <!-- grade-book -->
          <button
            @click="switchTab('grade book')"
            :class="[
              'flex w-full items-center gap-3 px-3 sm:px-4 py-2 text-sm font-medium rounded-lg transition-colors',
              activeTab === 'grade book'
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-700 hover:bg-gray-100',
            ]"
          >
            <svg
              class="w-5 h-5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
              />
            </svg>
            <span class="truncate">grade book</span>
          </button>
          <!-- live classes -->
          <button
            @click="switchTab('liveclassesattendence')"
            :class="[
              'flex w-full items-center gap-3 px-3 sm:px-4 py-2 text-sm font-medium rounded-lg transition-colors',
              activeTab === 'liveclassesattendence'
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-700 hover:bg-gray-100',
            ]"
          >
            <svg
              class="w-5 h-5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
              />
            </svg>
            <span class="truncate">live classes attendence</span>
          </button>

          <button
            @click="switchTab('internalmark')"
            :class="[
              'flex w-full items-center gap-3 px-3 sm:px-4 py-2 text-sm font-medium rounded-lg transition-colors',
              activeTab === 'internalmark'
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-700 hover:bg-gray-100',
            ]"
          >
            <svg
              class="w-5 h-5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 002.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 012.916.52 6.003 6.003 0 01-5.395 4.972m0 0a6.726 6.726 0 01-2.749 1.35m0 0a6.772 6.772 0 01-3.044 0"
              />
            </svg>
            <span class="truncate">internalmark</span>
          </button>
          <!-- NOTIFICATION -->
          <button
            @click="switchTab('notification')"
            :class="[
              'flex w-full items-center gap-3 px-3 sm:px-4 py-2 text-sm font-medium rounded-lg transition-colors',
              activeTab === 'notification'
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-700 hover:bg-gray-100',
            ]"
          >
            <svg
              class="w-5 h-5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
              />
            </svg>
            <span class="truncate">notification</span>
          </button>
          <!-- MESSAGE NOTIFICATION -->
          <button
            @click="switchTab('messagepage')"
            :class="[
              'flex w-full items-center gap-3 px-3 sm:px-4 py-2 text-sm font-medium rounded-lg transition-colors',
              activeTab === 'messagepage'
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-700 hover:bg-gray-100',
            ]"
          >
            <svg
              class="w-5 h-5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155"
              />
            </svg>
            <span class="truncate">message notification</span>
          </button>

          <!-- PROFILE -->
          <button
            @click="switchTab('profile')"
            :class="[
              'flex w-full items-center gap-3 px-3 sm:px-4 py-2 text-sm font-medium rounded-lg transition-colors',
              activeTab === 'profile'
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-700 hover:bg-gray-100',
            ]"
          >
            <svg
              class="w-5 h-5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.963 0a9 9 0 10-11.963 0m11.963 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <span class="truncate">profile</span>
          </button>

          <!-- ACCESSIBLE COURSE -->
          <button
            @click="switchTab('accessible')"
            :class="[
              'flex w-full items-center gap-3 px-3 sm:px-4 py-2 text-sm font-medium rounded-lg transition-colors',
              activeTab === 'accessible'
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-700 hover:bg-gray-100',
            ]"
          >
            <svg
              class="w-5 h-5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
              />
            </svg>
            <span class="truncate">accessible</span>
          </button>

          <!-- USER INSTRUCTION -->
          <button
            @click="switchTab('userinstruction')"
            :class="[
              'flex w-full items-center gap-3 px-3 sm:px-4 py-2 text-sm font-medium rounded-lg transition-colors',
              activeTab === 'userinstruction'
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-700 hover:bg-gray-100',
            ]"
          >
            <svg
              class="w-5 h-5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
              />
            </svg>
            <span class="truncate">user instruction</span>
          </button>

          <!-- MY DRIVE-->
          <button
            @click="switchTab('my drive')"
            :class="[
              'flex w-full items-center gap-3 px-3 sm:px-4 py-2 text-sm font-medium rounded-lg transition-colors',
              activeTab === 'my drive'
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-700 hover:bg-gray-100',
            ]"
          >
            <svg
              class="w-5 h-5 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12.75l1.5 1.5 4.5-4.5m5.25 3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span class="truncate">my drive</span>
          </button>
        </nav>
      </div>

      <!-- MAIN CONTENT -->
      <div
        ref="contentArea"
        class="flex-1 w-full min-w-0 max-w-6xl p-4 sm:p-6 md:p-8 lg:p-10 overflow-y-auto max-h-[calc(100vh-80px)] scroll-smooth"
      >
        <div class="pl-0 md:pl-6" v-if="activeTab === 'login'">
          <img
            class="w-full max-w-full h-auto object-contain rounded-lg shadow-sm border border-gray-200"
            src="@/assets/help/studentLoginpage1.png"
            alt="Student login page"
          />
          <div class="mt-5">
            <h1 class="text-lg sm:text-xl font-semibold text-blue-600">
              Features Available:
            </h1>
            <p class="text-sm text-gray-700 leading-relaxed">
              <br /><br />

              1) Email Authentication
              <br />Instructors can log in using their registered email address.
              <br />The system validates the email before granting access.

              <br /><br />

              2) Password Authentication
              <br />Instructors must enter their password to access their
              account. <br />Passwords are securely verified by the system.

              <br /><br />

              3) Show/Hide Password
              <br />An eye icon is provided in the password field.
              <br />Instructors can view or hide their password while typing.

              <br /><br />

              4) Forgot Password
              <br />A "Forgot Password?" link is available. <br />Instructors
              can reset their password through the password recovery process.

              <br /><br />

              5) Secure Login
              <br />Only authorized instructors can access the platform.
              <br />Invalid credentials are rejected with appropriate validation
              messages.

              <br /><br />

              6) Responsive User Interface
              <br />Clean and user-friendly login design. <br />Accessible from
              desktop and mobile devices.

              <br /><br />

              7) Branding and Instructor-Friendly Design
              <br />Institution branding/logo is displayed.
              <br />Instructor-focused visual design improves user experience.
            </p>
          </div>
        </div>

        <div class="pl-0 md:pl-6" v-if="activeTab === 'home'">
          <img
            class="w-full max-w-full h-auto object-contain rounded-lg shadow-sm border border-gray-200"
            src="@/assets/instrutorhomehelpin/homepage1.png"
            alt="Student home page"
          />
          <!-- <img
            class="w-full max-w-full h-auto object-contain rounded-lg shadow-sm border border-gray-200 mt-10"
            src="@/assets/instrutorhomehelpin/homepage2.png"
            alt="Student home page"
          /> -->
          <div class="mt-5">
            <h1 class="text-lg sm:text-xl font-semibold text-blue-600">
              Instructor Course Management Module
            </h1>

            <p class="text-sm text-gray-700 leading-relaxed mt-5">
              1) Instructor Dashboard Access — Instructors can access a
              personalized dashboard after login. It provides an overview of
              courses, teaching activities, and announcements.
              <br /><br />

              2) Assigned Courses — Displays all courses assigned to the
              instructor. Instructors can quickly access and manage their
              teaching courses.
              <br /><br />

              3) Course Preview — Each course card includes a
              <strong>Preview</strong> button to review course content before
              publishing or editing. <br /><br />

              4) Course Information — Displays detailed course information
              including Course Code, Program, Semester, Section, Course Type,
              Instructor Name, Publication Status, and Student Count.
              <br /><br />

              5) Course Management — Instructors can manage course content,
              organize learning materials, and monitor course progress from a
              single dashboard.
              <br /><br />

              6) Student Enrollment Overview — Displays the total number of
              students enrolled in each assigned course for quick monitoring.
              <br /><br />

              7) Quick Course Navigation — Course cards provide fast access to
              course management and related teaching activities.
              <br /><br />

              8) Announcements — Instructors can view important academic
              notifications and institutional announcements.
              <br /><br />

              9) Teaching Resources — Provides easy access to instructional
              materials, LMS documentation, and platform guides.
              <br /><br />

              10) View All Courses — Allows instructors to view and access all
              assigned courses from a centralized location.
              <br /><br />

              11) Secure Course Access — Only authorized instructors can access
              and manage assigned courses through secure authentication.
              <br /><br />

              12) Responsive User Interface — The dashboard is fully responsive
              and accessible on desktop, tablet, and mobile devices for
              convenient course management.
            </p>
          </div>
        </div>

        <div class="pl-0 md:pl-6" v-if="activeTab === 'course'">
          <img
            class="w-full max-w-full h-auto object-contain rounded-lg shadow-sm border border-gray-200"
            src="@/assets/instrutorhomehelpin/mycourse3.png"
            alt="My courses page"
          />
          <div class="mt-5">
            <h1 class="text-lg sm:text-xl font-semibold text-blue-600">
              My Courses Module
            </h1>

            <p class="text-sm text-gray-700 leading-relaxed mt-5">
              The My Courses page enables instructors to access, manage, and
              monitor all their assigned courses from a centralized dashboard.
            </p>

            <p class="text-sm text-gray-700 leading-relaxed">
              <br />

              1) Assigned Course List
              <br />Displays all courses assigned to the instructor in a
              structured card layout. <br />Provides quick access to every
              teaching course.

              <br /><br />

              2) Course Search
              <br />Search courses using the search box. <br />Allows
              instructors to quickly find a specific course.

              <br /><br />

              3) Advanced Filters
              <br />Filter courses by Batch, Program, Semester, Course Type,
              Instructor Email, Instructor Name, and Status. <br />Helps
              instructors locate courses efficiently.

              <br /><br />

              4) Sorting Options
              <br />Sort courses using the available sorting menu. <br />Makes
              course management more organized.

              <br /><br />

              5) Course Information
              <br />Each course card displays the Course Name, Course Code,
              Batch, Program, Semester, Section, Course Type, Instructor Name,
              Publish Status, and Student Count. <br />Provides complete course
              details at a glance.

              <br /><br />

              6) Course Thumbnail
              <br />Each course includes a visual banner or thumbnail.
              <br />Helps instructors quickly identify courses.

              <br /><br />

              7) Course Preview
              <br />A <strong>Preview</strong> button allows instructors to
              review course content before managing or publishing it.
              <br />Ensures course quality and accuracy.

              <br /><br />

              8) Quick Course Access
              <br />Course cards provide direct access to course-related
              activities and management features. <br />Improves navigation and
              productivity.

              <br /><br />

              9) Responsive User Interface
              <br />Accessible on desktop, tablet, and mobile devices.
              <br />Provides a consistent user experience across platforms.

              <br /><br />

              10) Secure Course Access
              <br />Only authorized instructors can access and manage their
              assigned courses. <br />Protects course information through secure
              authentication.
            </p>
          </div>
        </div>

        <div class="pl-0 md:pl-6" v-if="activeTab === 'student'">
          <img
            class="w-full max-w-full h-auto object-contain rounded-lg shadow-sm border border-gray-200"
            src="@/assets/instrutorhomehelpin/studentpage4.png"
            alt="Course learning page"
          />
          <div class="mt-5">
            <h1 class="text-lg sm:text-xl font-semibold text-blue-600">
              Student Management Module
            </h1>

            <p class="text-sm text-gray-700 leading-relaxed mt-5">
              The Student Management Module enables instructors to view,
              monitor, and manage students enrolled in a selected course from a
              centralized interface.
            </p>

            <p class="text-sm text-gray-700 leading-relaxed">
              1) Course Selection
              <br />Displays the selected course name along with batch and
              program details. <br />Provides quick access to course-specific
              student records.

              <br /><br />

              2) Student List
              <br />Displays all active students enrolled in the selected
              course. <br />Shows the total number of enrolled students.

              <br /><br />

              3) Student Search
              <br />Search for students using the search box. <br />Helps
              instructors quickly locate a specific student.

              <br /><br />

              4) Student Information
              <br />Displays each student's name, email address, and
              PRN/registration number. <br />Provides complete student
              identification details.

              <br /><br />

              5) Batch & Program Details
              <br />Shows the batch and academic program for each student.
              <br />Helps instructors identify student groups easily.

              <br /><br />

              6) Account Information
              <br />Displays the account creation date for every student.
              <br />Helps monitor student account activity.

              <br /><br />

              7) Last Login Tracking
              <br />Shows each student's most recent login date and time.
              <br />Allows instructors to monitor student engagement.

              <br /><br />

              8) Learning Progress
              <br />Displays the learning progress for each student. <br />Helps
              instructors identify students who require additional support.

              <br /><br />

              9) Student Communication
              <br />Quick navigation to Messages, Assignments, Forums, Live
              Classes, Assessments, and Projects. <br />Provides centralized
              access to course management features.

              <br /><br />

              10) Student Monitoring
              <br />Allows instructors to monitor participation and academic
              activities. <br />Supports effective course supervision.

              <br /><br />

              11) Secure Student Records
              <br />Only authorized instructors can access enrolled student
              information. <br />Ensures secure handling of academic records.

              <br /><br />

              12) Responsive User Interface
              <br />Accessible on desktop, tablet, and mobile devices.
              <br />Provides a consistent and user-friendly experience across
              all platforms.
            </p>
          </div>
        </div>

        <div class="pl-0 md:pl-6" v-if="activeTab === 'message'">
          <img
            class="w-full max-w-full h-auto object-contain rounded-lg shadow-sm border border-gray-200"
            src="@/assets/instrutorhomehelpin/messagepage5.png"
            alt="Messages page"
          />
          <div class="mt-5">
            <h1 class="text-lg sm:text-xl font-semibold text-blue-600">
              Course Messages Module
            </h1>

            <p class="text-sm text-gray-700 leading-relaxed mt-5">
              The Course Messages Module enables instructors to communicate with
              students, share learning resources, and manage course-related
              conversations from a centralized messaging interface.
            </p>

            <p class="text-sm text-gray-700 leading-relaxed">
              1) One-to-One Communication
              <br />Instructors can communicate directly with students enrolled
              in the selected course. <br />Complete conversation history is
              available for future reference.

              <br /><br />

              2) Student Contact List
              <br />Displays all students enrolled in the selected course.
              <br />Allows instructors to quickly select a student and start a
              conversation.

              <br /><br />

              3) Search Conversations
              <br />Search students or conversations using the search bar.
              <br />Helps instructors quickly locate previous chats.

              <br /><br />

              4) Real-Time Messaging
              <br />Send and receive instant messages with students.
              <br />Supports effective course-related communication.

              <br /><br />

              5) File Sharing
              <br />Instructors can share course documents, notes, assignments,
              and other learning resources. <br />Students can also submit files
              through the chat for review.

              <br /><br />

              6) Image & Media Sharing
              <br />Supports sharing images and other media files during
              conversations. <br />Enhances communication with visual content.

              <br /><br />

              7) Conversation History
              <br />All course conversations are securely stored. <br />Allows
              instructors to review previous discussions whenever required.

              <br /><br />

              8) Secure Communication
              <br />Only authorized instructors and students enrolled in the
              selected course can exchange messages. <br />Ensures secure and
              private communication.

              <br /><br />

              9) Course-Based Messaging
              <br />Messages are organized according to the selected course.
              <br />Keeps communication structured and easy to manage.

              <br /><br />

              10) Responsive User Interface
              <br />Accessible on desktop, tablet, and mobile devices.
              <br />Provides a smooth and consistent messaging experience across
              platforms.
            </p>
          </div>
        </div>

        <div class="pl-0 md:pl-6" v-if="activeTab === 'assignment'">
          <img
            class="w-full max-w-full h-auto object-contain rounded-lg shadow-sm border border-gray-200"
            src="@/assets/instrutorhomehelpin/assignments6.png"
            alt="Assignments page"
          />
          <div class="mt-5">
            <h1 class="text-lg sm:text-xl font-semibold text-blue-600">
              Assignment Management Module
            </h1>

            <p class="text-sm text-gray-700 leading-relaxed mt-5">
              The Assignment Management Module enables instructors to create,
              manage, review, and monitor course assignments from a centralized
              interface.
            </p>

            <p class="text-sm text-gray-700 leading-relaxed">
              1) View Assignments
              <br />Displays all assignments created for the selected course.
              <br />Provides quick access to assignment details, due dates, and
              attached files.

              <br /><br />

              2) Create Assignment
              <br />Instructors can create new assignments using the
              <strong>+ Add Assignment</strong> button. <br />Assignment details
              include title, description, due date, instructions, and
              attachments.

              <br /><br />

              3) Assignment Search
              <br />Search assignments using the search box. <br />Quickly
              locate assignments by title or keyword.

              <br /><br />

              4) Sorting Options
              <br />Assignments can be sorted using the available sorting menu.
              <br />Helps organize assignments efficiently.

              <br /><br />

              5) Assignment Details
              <br />Each assignment displays its title, description, due date,
              and attached file. <br />Provides complete information at a
              glance.

              <br /><br />

              6) File Download
              <br />Instructors can download assignment files directly from the
              assignment list. <br />Supports easy access to reference documents
              and attachments.

              <br /><br />

              7) Assignment Preview
              <br />The <strong>View</strong> option allows instructors to
              preview assignment details before editing or reviewing.
              <br />Ensures accurate assignment management.

              <br /><br />

              8) Edit Assignment
              <br />Assignments can be updated whenever necessary.
              <br />Instructors can modify assignment details, due dates, and
              attached files.

              <br /><br />

              9) Student Submission Review
              <br />Instructors can review student assignment submissions.
              <br />Supports evaluation and monitoring of submitted work.

              <br /><br />

              10) Assignment Feedback
              <br />The <strong>Feedback</strong> option allows instructors to
              provide comments and evaluation for student submissions.
              <br />Improves communication and learning outcomes.

              <br /><br />

              11) Secure Assignment Management
              <br />Only authorized instructors can create, edit, review, and
              manage course assignments. <br />Protects assignment information
              through secure authentication.

              <br /><br />

              12) Responsive User Interface
              <br />Accessible on desktop, tablet, and mobile devices.
              <br />Provides a consistent and user-friendly experience across
              all platforms.
            </p>
          </div>
        </div>

        <div class="pl-0 md:pl-6" v-if="activeTab === 'forums'">
          <img
            class="w-full max-w-full h-auto object-contain rounded-lg shadow-sm border border-gray-200"
            src="@/assets/instrutorhomehelpin/forumspage7.png"
            alt="Forums page"
          />
          <div class="mt-5">
            <h1 class="text-lg sm:text-xl font-semibold text-blue-600">
              Discussion Forum Module
            </h1>

            <p class="text-sm text-gray-700 leading-relaxed mt-5">
              The Discussion Forum Module enables instructors to create, manage,
              and monitor course discussion groups for effective collaboration
              and communication with students.
            </p>

            <p class="text-sm text-gray-700 leading-relaxed">
              1) Group Creation
              <br />Instructors can create discussion groups for a selected
              course. <br />Groups can be created for assignments, projects, or
              general academic discussions.

              <br /><br />

              2) Group Management
              <br />Instructors can edit or delete discussion groups.
              <br />Allows easy management of existing discussion forums.

              <br /><br />

              3) Search Groups
              <br />Search discussion groups using the search box. <br />Helps
              instructors quickly locate a specific group.

              <br /><br />

              4) Group Chat
              <br />Instructors and enrolled students can exchange messages
              within the selected discussion group. <br />Supports real-time
              academic communication and collaboration.

              <br /><br />

              5) File & Media Sharing
              <br />Supports sharing documents, images, and other learning
              resources within the discussion group. <br />Helps instructors
              distribute course materials efficiently.

              <br /><br />

              6) Student Collaboration
              <br />Students can participate in instructor-created discussion
              groups. <br />Encourages collaborative learning and knowledge
              sharing.

              <br /><br />

              7) Course-Based Discussions
              <br />All discussions are organized according to the selected
              course. <br />Keeps conversations relevant and well structured.

              <br /><br />

              8) Conversation History
              <br />All group messages are securely stored. <br />Allows
              instructors and students to review previous discussions whenever
              required.

              <br /><br />

              9) Secure Access
              <br />Only authorized instructors and enrolled students can access
              discussion groups. <br />Ensures secure and private course
              communication.

              <br /><br />

              10) Responsive User Interface
              <br />Accessible on desktop, tablet, and mobile devices.
              <br />Provides a consistent and user-friendly experience across
              all platforms.
            </p>
          </div>
        </div>

        <div class="pl-0 md:pl-6" v-if="activeTab === 'liveclass'">
          <img
            class="w-full max-w-full h-auto object-contain rounded-lg shadow-sm border border-gray-200"
            src="@/assets/instrutorhomehelpin/liveclassespage8.png"
            alt="Live class page"
          />
          <div class="mt-5">
            <h1 class="text-lg sm:text-xl font-semibold text-blue-600">
              Live Class Module
            </h1>

            <p class="text-sm text-gray-700 leading-relaxed mt-5">
              1) Live Class Creation
              <br />Instructors can create and schedule live classes for
              specific courses and batches. <br />Class schedules include title,
              meeting platform, start date, and end date. <br /><br />

              2) Meeting Platform Integration
              <br />Live classes can be integrated with Zoom, Microsoft Teams,
              or Google Meet. <br />Students can join sessions directly through
              the LMS using the meeting link. <br /><br />

              3) Class Scheduling Management
              <br />Instructors can reschedule, update, or cancel scheduled
              classes. <br />Students can view upcoming, ongoing, and completed
              classes. <br /><br />

              4) Join Class Access Control
              <br />The Join Class button is enabled only during the scheduled
              class duration. <br />Before the start time and after the end
              time, the button remains disabled. <br /><br />

              5) Class Status Management
              <br />Classes are automatically marked as Upcoming, Live, or
              Ended. <br />Status updates are based on the configured start and
              end time. <br /><br />

              6) Attendance Tracking
              <br />Student attendance is recorded automatically when joining a
              live session. <br />Instructors can view attendance reports and
              participation records. <br /><br />

              7) Session Recording Access
              <br />Recorded sessions can be uploaded or synced from the meeting
              platform. <br />Students can access recordings for revision and
              missed classes. <br /><br />

              8) Notifications and Reminders
              <br />Students receive notifications when a class is scheduled.
              <br />Automated reminders are sent before the class start time.
              <br /><br />

              9) Resource Sharing
              <br />Instructors can share documents, presentations, and
              reference materials. <br />Students can access all shared
              resources related to the live class. <br /><br />

              10) Feedback Collection
              <br />Students can submit feedback after attending a live class.
              <br />Instructors can review ratings and comments for improvement.
              <br /><br />

              11) Instructor Monitoring
              <br />Instructors can monitor attendance and student
              participation. <br />Announcements and important instructions can
              be shared during the session. <br /><br />

              12) Security and Access Control
              <br />Only enrolled students can access scheduled live classes.
              <br />Meeting access is restricted through LMS authentication.
              <br /><br />

              13) Reports and Analytics
              <br />Attendance, participation, and completion reports are
              available. <br />Administrators can monitor live class engagement
              and performance. <br /><br />

              14) Mobile and Cross-Platform Access
              <br />Students can join classes using desktop, tablet, or mobile
              devices. <br />The platform supports major browsers and operating
              systems. <br /><br />

              15) Feedback Management
              <br />Instructors can enable or disable feedback collection while
              creating or editing a live class. <br />Feedback settings are
              configured individually for each live session. <br /><br />

              16) Feedback Access Control
              <br />When feedback is enabled by the instructor, students can
              submit feedback after attending the class. <br />When feedback is
              disabled, the feedback option is not displayed to students.
              <br /><br />

              17) Student Feedback Submission
              <br />Students can provide ratings, comments, and suggestions for
              the live session. <br />Feedback can only be submitted by students
              enrolled in the class. <br /><br />

              18) Feedback Visibility
              <br />Submitted feedback is available to instructors and
              administrators for review. <br />Students cannot view feedback
              submitted by other participants unless permissions are granted.
              <br /><br />

              19) Feedback Reports
              <br />Instructors can analyze feedback ratings and comments to
              improve future sessions. <br />Feedback reports can be generated
              for individual classes or courses.
            </p>
          </div>
        </div>

        <div class="pl-0 md:pl-6" v-if="activeTab === 'assessment'">
          <img
            class="w-full max-w-full h-auto object-contain rounded-lg shadow-sm border border-gray-200"
            src="@/assets/instrutorhomehelpin/assessmentpage9.png"
            alt="Assessment page"
          />
          <div class="mt-5">
            <h1 class="text-lg sm:text-xl font-semibold text-blue-600">
              Assessment Module
            </h1>

            <p class="text-sm text-gray-700 leading-relaxed mt-5">
              The Assessment Module enables instructors to create, manage, and
              publish quizzes and assessments for students. It supports multiple
              attempts, progress tracking, feedback collection, and secure
              assessment delivery.
            </p>

            <p class="text-sm text-gray-700 leading-relaxed">
              1) Assessment Creation
              <br />Instructors can create module assessments and lesson
              quizzes. <br />Assessments can include multiple-choice,
              descriptive, and other supported question types. <br /><br />

              2) Assessment Publishing
              <br />Assessments can be saved as drafts or published. <br />Only
              published assessments are visible to enrolled students.
              <br /><br />

              3) Question Management
              <br />Instructors can add, edit, delete, and organize assessment
              questions. <br />Questions can be updated before publishing the
              assessment. <br /><br />

              4) Attempt Limit
              <br />Instructors can define the maximum number of attempts
              allowed. <br />Students cannot exceed the configured attempt
              limit. <br /><br />

              5) Shuffle Questions
              <br />Question order can be randomized for every student attempt.
              <br />This helps reduce repetitive question patterns during
              assessments. <br /><br />

              6) Assessment Availability
              <br />Only students enrolled in the course can access available
              assessments. <br />Availability is controlled by the instructor's
              publishing settings. <br /><br />

              7) Student Attempts
              <br />The system records every student attempt automatically.
              <br />Instructors can monitor the number of attempts used by each
              student. <br /><br />

              8) Assessment Results
              <br />Student responses are securely stored after submission.
              <br />Results can be reviewed according to the assessment
              settings. <br /><br />

              9) Feedback Management
              <br />Instructors can enable or disable feedback for each
              assessment. <br />Students can submit feedback only when feedback
              is enabled. <br /><br />

              10) Search Assessments
              <br />The search option allows instructors to quickly locate
              assessments. <br />Assessments can be searched by title or
              keyword. <br /><br />

              11) Assessment Editing
              <br />Instructors can modify assessment details before students
              begin attempting them. <br />Published assessments can be updated
              based on system permissions. <br /><br />

              12) Student Performance Monitoring
              <br />Instructors can review assessment completion and student
              participation. <br />Performance data helps evaluate learning
              outcomes. <br /><br />

              13) Secure Access
              <br />Only authorized instructors can create or manage
              assessments. <br />Only enrolled students can attempt published
              assessments. <br /><br />

              14) Responsive Interface
              <br />The Assessment Module works on desktop, tablet, and mobile
              devices. <br />Provides a consistent assessment experience across
              supported platforms.
            </p>
          </div>
        </div>

        <div class="pl-0 md:pl-6" v-if="activeTab === 'project'">
          <img
            class="w-full max-w-full h-auto object-contain rounded-lg shadow-sm border border-gray-200"
            src="@/assets/instrutorhomehelpin/projectpage10.png"
            alt="Project / team leader page"
          />
          <div class="mt-5">
            <h1 class="text-lg sm:text-xl font-semibold text-blue-600">
              Projects Module
            </h1>

            <p class="text-sm text-gray-700 leading-relaxed mt-5">
              The Projects Module enables instructors to create project teams,
              assign students, monitor project progress, and manage team-based
              learning activities throughout the course.
            </p>

            <p class="text-sm text-gray-700 leading-relaxed">
              1) Project Team Creation
              <br />Instructors can create multiple project teams for a course.
              <br />Each team is assigned a unique name and project workspace.
              <br /><br />

              2) Student Assignment
              <br />Students can be assigned to specific project teams by the
              instructor. <br />Only assigned students can access their
              respective project team. <br /><br />

              3) Team Leader Assignment
              <br />Instructors can designate a team leader for each project.
              <br />The team leader coordinates project activities and team
              communication. <br /><br />

              4) Team Member Management
              <br />Instructors can add or remove members from a project team.
              <br />The total number of team members is displayed for each
              project. <br /><br />

              5) Project Workspace
              <br />Each project has a dedicated workspace for collaboration.
              <br />Team members can work together throughout the project
              lifecycle. <br /><br />

              6) Team Collaboration
              <br />Students can collaborate with their assigned teammates.
              <br />Project discussions and resource sharing are limited to team
              members. <br /><br />

              7) Progress Monitoring
              <br />Instructors can monitor project participation and overall
              team progress. <br />Project status can be reviewed throughout the
              course. <br /><br />

              8) Project Submission
              <br />Teams can submit project deliverables before the specified
              deadline. <br />Submitted projects are available for instructor
              review and evaluation. <br /><br />

              9) Secure Access
              <br />Only authorized instructors can create and manage project
              teams. <br />Students can access only the projects assigned to
              them. <br /><br />

              10) Responsive Interface
              <br />The Projects Module is accessible on desktop, tablet, and
              mobile devices. <br />Provides a consistent collaboration
              experience across all supported platforms.
            </p>
          </div>
        </div>

        <div class="pl-0 md:pl-6" v-if="activeTab === 'grading'">
          <img
            class="w-full max-w-full h-auto object-contain rounded-lg shadow-sm border border-gray-200"
            src="@/assets/instrutorhomehelpin/gradingpage11.png"
            alt="Project / team leader page"
          />
          <div class="mt-5">
            <h1 class="text-lg sm:text-xl font-semibold text-blue-600">
              Grading Module
            </h1>

            <p class="text-sm text-gray-700 leading-relaxed mt-5">
              The Grading Module enables instructors to monitor student course
              completion, manage certificate eligibility, and view certificate
              records for enrolled students.
            </p>

            <p class="text-sm text-gray-700 leading-relaxed">
              1) Certificate Records
              <br />Displays all students who are eligible for course
              certificates. <br />Each record includes student details, course
              progress, and certificate status. <br /><br />

              2) Student Information
              <br />Displays the student's name and registered email address.
              <br />Helps instructors quickly identify certificate recipients.
              <br /><br />

              3) Course Progress
              <br />Shows each student's overall course completion percentage.
              <br />Progress helps determine certificate eligibility.
              <br /><br />

              4) Course Duration
              <br />Displays the student's course start date and completion
              date. <br />Allows instructors to monitor the learning timeline.
              <br /><br />

              5) Certificate Preview
              <br />Instructors can view generated certificates before sharing
              them. <br />Certificates can be verified for accuracy and
              completeness. <br /><br />

              6) Certificate Status
              <br />Certificate availability can be enabled or disabled.
              <br />Only active certificates are accessible to eligible
              students. <br /><br />

              7) Search Records
              <br />The search option allows instructors to quickly find
              certificate records. <br />Students can be searched using their
              name or email address. <br /><br />

              8) Student Eligibility
              <br />Certificates are issued only after the required course
              completion criteria are met. <br />Students who have not completed
              the course remain ineligible. <br /><br />

              9) Secure Access
              <br />Only authorized instructors can access and manage
              certificate records. <br />Students can view only their own
              certificates after they become available. <br /><br />

              10) Responsive Interface
              <br />The Grading Module is accessible on desktop, tablet, and
              mobile devices. <br />Provides a consistent user experience across
              supported platforms.
            </p>
          </div>
        </div>

        <div class="pl-0 md:pl-6" v-if="activeTab === 'lesson polls'">
          <img
            class="w-full max-w-full h-auto object-contain rounded-lg shadow-sm border border-gray-200"
            src="@/assets/instrutorhomehelpin/lessonpollspage12.png"
            alt="Project / team leader page"
          />
          <div class="mt-5">
            <h1 class="text-lg sm:text-xl font-semibold text-blue-600">
              Lesson Polls Module
            </h1>

            <p class="text-sm text-gray-700 leading-relaxed mt-5">
              1) Poll Creation
              <br />Instructors can create lesson polls to collect student
              opinions, understanding, or feedback during a course. <br />Each
              poll can include a question with multiple answer options.
              <br /><br />

              2) Poll Publishing
              <br />Instructors can publish or unpublish polls at any time.
              <br />Only published polls are visible to enrolled students.
              <br /><br />

              3) Student Participation
              <br />Students can participate by selecting one answer for each
              poll. <br />Each student can submit only one response unless
              multiple attempts are allowed by the instructor. <br /><br />

              4) Live Result Tracking
              <br />The system automatically records every student response.
              <br />Instructors can view the total number of responses received
              for each option. <br /><br />

              5) View Answered Students
              <br />Instructors can view the list of students who selected each
              answer option. <br />This helps analyze student participation and
              understanding. <br /><br />

              6) Poll Result Analysis
              <br />Response counts and percentages are calculated
              automatically. <br />Results help instructors evaluate classroom
              engagement and learning outcomes. <br /><br />

              7) Poll Editing
              <br />Instructors can edit or update poll questions before
              students submit responses. <br />Once responses are collected,
              editing may be restricted to maintain data integrity. <br /><br />

              8) Poll Deletion
              <br />Instructors can delete unused or inactive polls.
              <br />Deleted polls are removed from student access. <br /><br />

              9) Access Control
              <br />Only instructors can create, edit, publish, or delete lesson
              polls. <br />Only students enrolled in the corresponding course
              can participate in the poll. <br /><br />

              10) Student Response Reports
              <br />Instructors can review detailed reports showing student
              responses and participation statistics. <br />Reports can be used
              to monitor student engagement and lesson effectiveness.
            </p>
          </div>
        </div>

        <div class="pl-0 md:pl-6" v-if="activeTab === 'grade book'">
          <img
            class="w-full max-w-full h-auto object-contain rounded-lg shadow-sm border border-gray-200"
            src="@/assets/instrutorhomehelpin/gradebookpage13.png"
            alt="Grade book page"
          />
          <div class="mt-5">
            <h1 class="text-lg sm:text-xl font-semibold text-blue-600">
              Grade Book Module
            </h1>

            <p class="text-sm text-gray-700 leading-relaxed mt-5">
              1) Student Grade Overview
              <br />Instructors can view the overall academic progress of
              students for assignments and assessments. <br />The Grade Book
              displays completed work, marks, and performance summaries.
              <br /><br />

              2) Assignment Grade Records
              <br />The Grade Book lists all assignments along with the total
              number of students and completed submissions. <br />Instructors
              can monitor assignment completion rates and download submission
              records. <br /><br />

              3) Assessment Grade Records
              <br />Assessment results are displayed separately from
              assignments. <br />Quiz scores, attempts, and completion details
              are available for each assessment. <br /><br />

              4) Student Performance Tracking
              <br />Instructors can track individual student performance across
              assignments and assessments. <br />Performance reports help
              identify students requiring additional academic support.
              <br /><br />

              5) Completion Statistics
              <br />The system displays the total number of enrolled students
              and the number of students who completed each assignment or
              assessment. <br />Completion statistics are updated automatically.
              <br /><br />

              6) Download Reports
              <br />Instructors can download assignment and assessment reports
              for offline review. <br />Reports include student participation
              and completion information. <br /><br />

              7) Search and Filter
              <br />The Grade Book provides a search option to quickly locate
              assignments or assessments. <br />Results can be filtered based on
              course content. <br /><br />

              8) Progress Monitoring
              <br />The Grade Book helps instructors monitor course progress
              throughout the semester. <br />Student completion status is
              updated whenever new submissions are recorded. <br /><br />

              9) Access Control
              <br />Only authorized instructors and administrators can access
              the Grade Book. <br />Students can view only their own grades and
              performance records where permitted. <br /><br />

              10) Reports and Analytics
              <br />The system generates reports showing assignment completion,
              assessment performance, and overall course progress. <br />These
              reports assist instructors in evaluating student performance and
              academic outcomes.
            </p>
          </div>
        </div>
        <!-- live classes attendance -->
        <div class="pl-0 md:pl-6" v-if="activeTab === 'liveclassesattendence'">
          <img
            class="w-full max-w-full h-auto object-contain rounded-lg shadow-sm border border-gray-200"
            src="@/assets/instrutorhomehelpin/liveclassesattendance14.png"
            alt="Live class attendance page"
          />

          <div class="mt-5">
            <h1 class="text-lg sm:text-xl font-semibold text-blue-600">
              Live Class Attendance Module
            </h1>

            <p class="text-sm text-gray-700 leading-relaxed mt-5">
              1) Attendance Tracking
              <br />The system automatically records student attendance when
              they join a scheduled live class. <br />Attendance records include
              the class title, meeting start time, and meeting end time.
              <br /><br />

              2) Attendance Overview
              <br />Instructors can view attendance statistics for every live
              class. <br />The system displays the total number of students who
              were present and absent for each session. <br /><br />

              3) Live Class Attendance List
              <br />A complete attendance list is maintained for all scheduled
              live classes. <br />Each record includes the class title, meeting
              schedule, attendance count, and creation date. <br /><br />

              4) Student Attendance Details
              <br />Instructors can review attendance details for individual
              live class sessions. <br />Attendance information helps identify
              students who attended or missed the class. <br /><br />

              5) Attendance Reports
              <br />Attendance reports can be downloaded for offline review and
              record keeping. <br />Reports assist instructors in monitoring
              student participation. <br /><br />

              6) Search Attendance Records
              <br />The attendance list includes a search option for quickly
              locating specific live classes. <br />Search results are filtered
              based on the class title or related information. <br /><br />

              7) Attendance Analytics
              <br />Present and absent counts are calculated automatically after
              each live class ends. <br />Attendance statistics help instructors
              evaluate student engagement. <br /><br />

              8) Attendance History
              <br />The system stores attendance records for all completed live
              class sessions. <br />Historical records can be reviewed at any
              time by authorized users. <br /><br />

              9) Access Control
              <br />Only authorized instructors and administrators can access
              attendance reports. <br />Students can view only their own
              attendance records where permitted. <br /><br />

              10) Attendance Monitoring
              <br />Attendance records support academic reporting and course
              monitoring. <br />Administrators and instructors can use
              attendance data to improve student participation and identify
              absentee patterns.
            </p>
          </div>
        </div>

        <div class="pl-0 md:pl-6" v-if="activeTab === 'internalmark'">
          <img
            class="w-full max-w-full h-auto object-contain rounded-lg shadow-sm border border-gray-200"
            src="@/assets/instrutorhomehelpin/internalmark15.png"
            alt="internal mark"
          />

          <div class="mt-5">
            <h1 class="text-lg sm:text-xl font-semibold text-blue-600">
              Internal Mark Module
            </h1>

            <p class="text-sm text-gray-700 leading-relaxed mt-5">
              1) Internal Mark Overview
              <br />The Internal Mark module allows instructors and
              administrators to view students' overall academic performance.
              <br />Marks from assignments and assessments are combined to
              calculate the overall internal mark. <br /><br />

              2) Student Mark List
              <br />The module displays a list of enrolled students with their
              academic records. <br />Each student record includes Name, Email,
              PRN, Assignment Marks, Assessment Marks, and Overall Marks.
              <br /><br />

              3) Assignment Marks
              <br />The Assignment column displays the marks obtained by
              students in assignments. <br />Assignment marks are automatically
              updated after grading is completed. <br /><br />

              4) Assessment Marks
              <br />The Assessment column displays the marks scored in quizzes
              and assessments. <br />Assessment scores are calculated based on
              submitted attempts and instructor grading settings. <br /><br />

              5) Overall Internal Mark
              <br />The Overall Mark column displays the total internal marks
              calculated from assignment and assessment scores. <br />The
              overall score helps instructors evaluate student performance
              throughout the course. <br /><br />

              6) Student Selection
              <br />Instructors can select one or multiple students using the
              checkbox provided beside each student record. <br />Selected
              records can be used for bulk academic operations. <br /><br />

              7) Search Students
              <br />The Search option allows instructors to quickly find
              students by name, email, or PRN. <br />Search results are updated
              instantly based on the entered keyword. <br /><br />

              8) Download Internal Marks
              <br />The Download button exports the internal mark report.
              <br />Reports can be downloaded for academic review,
              documentation, or record keeping. <br /><br />

              9) Performance Monitoring
              <br />Instructors can monitor each student's academic progress
              through internal marks. <br />The module helps identify students
              who require additional academic support. <br /><br />

              10) Secure Access
              <br />Only authorized instructors and administrators can access
              internal mark records. <br />Student marks are protected through
              LMS authentication and access control permissions.
            </p>
          </div>
        </div>

        <div class="pl-0 md:pl-6" v-if="activeTab === 'notification'">
          <img
            class="w-full max-w-full h-auto object-contain rounded-lg shadow-sm border border-gray-200"
            src="@/assets/instrutorhomehelpin/notificationpage16.png"
            alt="Notification page"
          />
          <div class="mt-5">
            <h1 class="text-lg sm:text-xl font-semibold text-blue-600">
              Student Notification Read/Unread Module
            </h1>

            <p class="text-sm text-gray-700 leading-relaxed mt-5">
              1) Student Read/Unread Notification Management
              <br />Implemented read and unread status tracking for all student
              notifications. <br />Unread notifications are clearly highlighted
              for better visibility. <br />Notifications are automatically
              marked as read when accessed by the student. <br /><br />

              2) Student Unread Notification Badge Counter
              <br />The notification badge displays the total number of unread
              notifications for the student. <br />The unread count is updated
              in real time and decreases automatically when notifications are
              marked as read. <br /><br />

              3) Student Notification List Display
              <br />Notifications are displayed in a structured and
              user-friendly format. <br />Each notification includes an icon,
              notification message, and date/time information. <br />Read and
              unread notifications are visually distinguished for easier
              identification. <br /><br />

              4) Student Notification Categories
              <br />Students receive notifications related to courses,
              assignments, assessments, projects, forums, live classes, grading
              updates, and attendance activities. <br />All important academic
              updates are centralized within the notification module for easy
              access. <br /><br />

              5) Pagination Improvements
              <br />Added proper pagination support for the notification list.
              <br />Students can navigate between pages using previous and next
              controls. <br />Current page information and total page count are
              displayed accurately. <br />Notification data loads correctly
              across all pages. <br /><br />

              6) Performance and User Experience Enhancements
              <br />Improved notification retrieval and rendering. <br />Ensured
              synchronization between notification status and unread count.
              <br />Optimized navigation and notification management for a
              smoother student experience.
            </p>
          </div>
        </div>

        <div class="pl-0 md:pl-6" v-if="activeTab === 'messagepage'">
          <img
            class="w-full max-w-full h-auto object-contain rounded-lg shadow-sm border border-gray-200"
            src="@/assets/instrutorhomehelpin/messagepage14.png"
            alt="Message notification page"
          />
          <div class="mt-5">
            <h1 class="text-lg sm:text-xl font-semibold text-blue-600">
              Message Notification Module
            </h1>

            <p class="text-sm text-gray-700 leading-relaxed mt-5">
              1) Read / Unread Message Notification Status
              <br />Implemented read and unread status tracking for all message
              notifications. <br />Unread message notifications are clearly
              highlighted for better visibility. <br />Notifications are
              automatically marked as read when viewed by the user. <br /><br />

              2) Unread Message Badge Counter
              <br />The message badge displays the total number of unread
              messages. <br />Unread counts are updated automatically in real
              time when messages are read. <br />The badge count remains
              synchronized with the notification status. <br /><br />

              3) Message Notification List Display
              <br />Message notifications are displayed in a structured and
              user-friendly format. <br />Each notification includes the sender
              name, course information, message preview, and timestamp.
              <br />Read and unread notifications are visually distinguished for
              easy identification. <br /><br />

              4) One-to-One Chat Redirection
              <br />Clicking a message notification redirects the user directly
              to the corresponding chat conversation. <br />Users can quickly
              view and respond to messages without manually searching for the
              sender. <br /><br />

              5) Pagination Support
              <br />Added pagination support for message notifications.
              <br />Users can navigate through notification history using page
              controls. <br />Current page information and total page count are
              displayed accurately. <br />Notification data loads correctly
              across all pages. <br /><br />

              6) Performance and User Experience Enhancements
              <br />Improved message notification retrieval and rendering
              performance. <br />Optimized synchronization between unread counts
              and notification status. <br />Enhanced navigation and overall
              messaging experience for users.
            </p>
          </div>
        </div>

        <div class="pl-0 md:pl-6" v-if="activeTab === 'profile'">
          <img
            class="w-full max-w-full h-auto object-contain rounded-lg shadow-sm border border-gray-200"
            src="@/assets/instrutorhomehelpin/profilepage17.png"
            alt="Profile page"
          />
          <div class="mt-5">
            <h1 class="text-lg sm:text-xl font-semibold text-blue-600">
              User Profile Details & Profile Edit Module
            </h1>

            <p class="text-sm text-gray-700 leading-relaxed mt-5">
              1) Profile Details Management
              <br />Implemented a dedicated profile details page with a clean
              and organized layout. <br />Users can view personal information
              including Full Name, Role, Email Address, Mobile Number, Program,
              Address, and Description. <br />Profile information is presented
              in a structured format for improved readability and accessibility.
              <br /><br />

              2) Profile Image Management
              <br />Added profile avatar display functionality within the
              profile section. <br />Users can upload, update, and manage their
              profile picture. <br />Profile image changes are reflected
              immediately across the system. <br /><br />

              3) Profile Edit Functionality
              <br />Implemented profile editing capabilities for updating
              personal information. <br />Users can modify editable profile
              fields directly from the profile page. <br />Added validation and
              update handling to ensure data accuracy and consistency.
              <br /><br />

              4) Password Management
              <br />Added a secure Change Password feature. <br />Users can
              update their account password through a dedicated password
              management interface. <br />Implemented password validation and
              security checks during updates. <br /><br />

              5) Accessible Course Integration
              <br />Added an Accessible Course section within the profile
              module. <br />Users can quickly view courses assigned or available
              to them from the profile area. <br />Improved navigation between
              profile information and course-related details. <br /><br />

              6) UI & User Experience Enhancements
              <br />Redesigned the profile card and information layout for a
              modern appearance. <br />Improved responsiveness across desktop,
              tablet, and mobile devices. <br />Enhanced overall profile
              management and account settings experience.
            </p>
          </div>
        </div>

        <div class="pl-0 md:pl-6" v-if="activeTab === 'accessible'">
          <img
            class="w-full max-w-full h-auto object-contain rounded-lg shadow-sm border border-gray-200"
            src="@/assets/instrutorhomehelpin/accessiblepage18.png"
            alt="Accessible course page"
          />
          <div class="mt-5">
            <h1 class="text-lg sm:text-xl font-semibold text-blue-600">
              Accessible Course Module
            </h1>

            <p class="text-sm text-gray-700 leading-relaxed mt-5">
              1) Accessible Course List
              <br />Implemented an Accessible Course tab within the user profile
              module. <br />Displays only the courses assigned and accessible to
              the logged-in user. <br />Provides a centralized view of all
              available courses for quick access. <br /><br />

              2) Course Counter
              <br />Added a course counter to display the total number of
              accessible courses. <br />The count is updated automatically based
              on assigned course records. <br /><br />

              3) Course Search Functionality
              <br />Implemented search functionality to quickly locate courses.
              <br />Users can search using course title or course code.
              <br />Search results are filtered instantly for improved
              usability. <br /><br />

              4) Course Table Display
              <br />Displayed course information in a structured table format.
              <br />Each course record includes Serial Number, Course Title,
              Course Code, and Created Date. <br />The table layout improves
              readability and course management. <br /><br />

              5) Rows Per Page Control
              <br />Added rows-per-page selection for customizing the number of
              course records displayed. <br />Users can control the amount of
              data shown on each page for easier navigation. <br /><br />

              6) Pagination Support
              <br />Implemented pagination controls for navigating through
              course records. <br />Current page information and total page
              count are displayed accurately. <br />Course data loads
              efficiently across all pages. <br /><br />

              7) UI & User Experience Enhancements
              <br />Improved table design, spacing, and responsiveness.
              <br />Optimized the accessible course interface for desktop,
              tablet, and mobile devices. <br />Enhanced overall navigation and
              course viewing experience.
            </p>
          </div>
        </div>

        <div class="pl-0 md:pl-6" v-if="activeTab === 'userinstruction'">
          <img
            class="w-full max-w-full h-auto object-contain rounded-lg shadow-sm border border-gray-200"
            src="@/assets/instrutorhomehelpin/userinstructionpage19.png"
            alt="Student instructions page"
          />
          <div class="mt-5">
            <h1 class="text-lg sm:text-xl font-semibold text-blue-600">
              Student Instructions Module
            </h1>

            <p class="text-sm text-gray-700 leading-relaxed mt-5">
              1) Instruction Document Management
              <br />Implemented a centralized module for displaying student
              instruction documents. <br />Users can view all published
              instructions in a structured table format. <br />Instruction
              records include Title, Year, PDF Document, and Comments.
              <br /><br />

              2) Instruction List Display
              <br />Displayed instruction documents in an organized and
              user-friendly table. <br />Each record is assigned a serial number
              for easy reference. <br />Information is presented clearly for
              improved readability and accessibility. <br /><br />

              3) PDF Document Access & Download
              <br />Added support for viewing and downloading instruction
              documents in PDF format. <br />Users can access important academic
              guidelines, notices, and reference materials directly from the
              module. <br />Documents are available for future reference and
              offline use. <br /><br />

              4) Search Functionality
              <br />Implemented search functionality to quickly locate
              instruction documents. <br />Users can search by title, year,
              comments, or related document information. <br />Search results
              are filtered dynamically for faster document access. <br /><br />

              5) Comments & Additional Information
              <br />Added comments support for providing supplementary
              information related to each instruction document. <br />Helps
              users understand the purpose and context of published
              instructions. <br /><br />

              6) Pagination & Data Management
              <br />Implemented pagination support for managing large numbers of
              instruction records. <br />Added rows-per-page controls to
              customize the number of records displayed. <br />Ensures smooth
              navigation and efficient data loading across all pages.
              <br /><br />

              7) User Experience Enhancements
              <br />Improved document accessibility and navigation throughout
              the module. <br />Optimized table responsiveness for desktop,
              tablet, and mobile devices. <br />Provides quick and convenient
              access to important institutional instructions and academic
              resources.
            </p>
          </div>
        </div>
        <div class="pl-0 md:pl-6" v-if="activeTab === 'my drive'">
          <img
            class="w-full max-w-full h-auto object-contain rounded-lg shadow-sm border border-gray-200"
            src="@/assets/instrutorhomehelpin/mydrivepage19.png"
            alt="Task management page"
          />
          <div class="mt-5">
            <h1 class="text-lg sm:text-xl font-semibold text-blue-600">
              My Drive Module
            </h1>

            <p class="text-sm text-gray-700 leading-relaxed mt-5">
              1) File & Folder Management
              <br />Implemented a centralized My Drive module for storing and
              organizing academic files and folders. <br />Users can create new
              folders using the New button. <br />Provides a structured
              workspace for managing learning resources and documents.
              <br /><br />

              2) Folder Listing & Organization
              <br />Added folder count display to show the total number of
              available folders. <br />Folders are displayed in an organized
              grid view for easy navigation. <br />Each folder displays its name
              and creation date. <br /><br />

              3) Search Functionality
              <br />Implemented file and folder search functionality.
              <br />Users can quickly locate folders using folder names or
              keywords. <br />Search results are updated dynamically for a
              better user experience. <br /><br />

              4) Grid & List View Support
              <br />Added view toggle options for Grid View and List View.
              <br />Users can switch between layouts based on their preference.
              <br />Improves accessibility and file browsing efficiency.
              <br /><br />

              5) Folder Actions Management
              <br />Implemented action menu options for each folder. <br />Users
              can perform operations such as opening, editing, renaming, or
              managing folder contents. <br />Provides flexible file
              organization capabilities. <br /><br />

              6) Navigation & Drive Structure
              <br />Added breadcrumb navigation for easy location tracking.
              <br />Users can quickly navigate between Home and My Drive
              sections. <br />Supports a structured hierarchical file management
              system. <br /><br />

              7) Academic Resource Storage
              <br />Designed to store course materials, assignments, notes, and
              academic documents. <br />Provides quick access to educational
              resources through categorized folders. <br /><br />

              8) User Experience Enhancements
              <br />Implemented responsive design for desktop, tablet, and
              mobile devices. <br />Improved folder loading performance and
              navigation experience. <br />Enhanced usability with intuitive
              controls and clean visual layouts.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Header from "@/components/Instructor/Header.vue";

export default {
  components: {
    Header,
  },
  data() {
    return {
      activeTab: "login",
      showSidebar: false,
    };
  },
  methods: {
    switchTab(tab) {
      this.activeTab = tab;
      this.showSidebar = false;
      this.$nextTick(() => {
        if (this.$refs.contentArea) {
          this.$refs.contentArea.scrollTop = 0;
        }
      });
    },
  },
};
</script>

<style></style>
